import { NavLink } from 'react-router-dom';
import type { FunctionComponent, SVGProps } from 'react';
import {
  LogoShieldIcon,
  CaretIcon,
  UNavEngagementsIcon,
  UNavProjectsIcon,
  UNavDirectionsIcon,
  UNavInvitationsIcon,
  UNavCitizenIcon,
  UNavProgressIcon,
  UNavReportsIcon,
  UNavLearningIcon,
} from '../../assets/icons';

interface NavItem {
  to: string;
  label: string;
  Icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  hasChevron?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/user/engagements', label: 'Engagements', Icon: UNavEngagementsIcon },
  { to: '/user/projects', label: 'Projects', Icon: UNavProjectsIcon },
  { to: '/user/directions', label: 'Daily Directions', Icon: UNavDirectionsIcon },
  { to: '/user/invitations', label: 'Invitations', Icon: UNavInvitationsIcon, hasChevron: true },
  { to: '/user/citizen-requests', label: 'Citizen Requests', Icon: UNavCitizenIcon },
  { to: '/user/progress', label: 'Progress Dashboard', Icon: UNavProgressIcon },
  { to: '/user/reports', label: 'Reports', Icon: UNavReportsIcon },
  { to: '/user/learning', label: 'Learning Center', Icon: UNavLearningIcon },
];

interface UserSidebarProps {
  open?: boolean;
  onClose?: () => void;
}

export default function UserSidebar({ open = false, onClose }: UserSidebarProps) {
  return (
    <>
      {open && (
        <div
          className="fixed inset-x-0 bottom-0 top-16 z-20 bg-black/30 lg:hidden"
          onClick={onClose}
          aria-hidden
        />
      )}
      <aside
        className={[
          'fixed left-0 top-16 z-30 flex h-[calc(100vh-64px)] w-[280px] flex-col gap-6 border-r border-line-soft bg-white pl-4 pr-[17px] py-4 transition-transform duration-200 lg:z-10 lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        {/* Admin profile card */}
        <div className="flex items-center gap-3 rounded-xl bg-brand-shade p-4">
          <span className="flex items-center justify-center rounded-lg bg-brand p-2 text-white">
            <LogoShieldIcon className="size-5" />
          </span>
          <div className="flex flex-col">
            <span className="text-xs font-bold leading-4 text-ink">CHUNAAV Admin</span>
            <span className="text-[10px] font-medium uppercase leading-[15px] tracking-[0.5px] text-brand">
              System Administrator
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col gap-1">
          {NAV_ITEMS.map(({ to, label, Icon, hasChevron }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) =>
                [
                  'flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium no-underline transition-colors',
                  isActive
                    ? 'bg-brand-shade text-brand'
                    : 'text-ink-secondary hover:bg-brand-shade/60 hover:text-brand',
                ].join(' ')
              }
            >
              <Icon className="size-5 shrink-0" />
              <span className="leading-5">{label}</span>
              {hasChevron && <CaretIcon className="ml-auto size-4 shrink-0" />}
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}
